import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, Path } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'lodash';
import styled from 'styled-components';
import * as zod from 'zod';
import { dayjs } from '@lgg/isomorphic/dayjs';
import {
  ContactInteractionDirection,
  Mutation,
  MutationCreateManualContactInteractionArgs,
  MutationDeleteContactInteractionArgs,
  MutationUpdateContactInteractionArgs,
  Query,
  QueryContactInteractionArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useActiveDepartmentSelectOptions } from 'src/components/domain/departments/hooks/use-active-department-select-options';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { LggDateTimePicker } from 'src/components/general/inputs/custom-date-pickers/lgg-date-time-picker';
import {
  InlineTextArea,
  InlineInputWrapper,
} from 'src/components/general/inputs/inline-text-inputs';
import { SelectOption } from 'src/components/general/inputs/select/select';
import { InlineFormModal } from 'src/components/general/modals/inline-form/inline-form-modal';
import {
  AddExtraFieldButton,
  ExtraOptionIcon,
  InlineFormContactSelect,
  InlineFormUserSelect,
  UserSelectPlaceholder,
  UserSelectValue,
  useShowCloseConfirmationModal,
} from 'src/components/general/modals/inline-form/shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useCurrentUserId } from 'src/hooks/use-current-user';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useModalUrl } from 'src/hooks/use-modal-url';
import { useUrls } from 'src/hooks/use-urls';
import { useVisible } from 'src/hooks/use-visible';
import { copyUrlToClipboard } from 'src/utils/copy-to-clipboard-helpers';
import { Icon } from '../../icon';
import { InlineFormSelect } from './inline-form-select';

const MANUAL_INTERACTION_TYPES = [
  'CALL',
  'EMAIL',
  'SMS',
  'FACEBOOK_MESSENGER',
  'WHATSAPP',
  'INSTAGRAM',
  'WALK_IN',
] as const;

type ManualInteractionType = (typeof MANUAL_INTERACTION_TYPES)[number];

const GET_CONTACT_INTERACTION = gql`
  query GetManualContactInteraction($id: Int!) {
    contactInteraction(id: $id) {
      ... on ContactInteraction {
        id
        __typename
        occurredAt
        description
        channel {
          slug
        }
        department {
          id
        }
        contact {
          id
        }
        participatingEntity {
          id
          type
        }
        createdByUser {
          id
        }
      }
      ... on ContactInteractionWithDirection {
        direction
      }
    }
  }
`;

const CREATE_MANUAL_CONTACT_INTERACTION_MUTATION = gql`
  mutation CreateManualContactInteraction($input: CreateManualContactInteractionInput!) {
    createManualContactInteraction(input: $input) {
      ... on ContactInteraction {
        id
      }
    }
  }
`;

const UPDATE_CONTACT_INTERACTION_MUTATION = gql`
  mutation UpdateContactInteraction($id: Int!, $input: UpdateContactInteractionInput!) {
    updateContactInteraction(id: $id, input: $input) {
      ... on ContactInteraction {
        id
        occurredAt
        description
        department {
          id
        }
        participatingEntity {
          id
          type
        }
      }
      ... on ContactInteractionWithDirection {
        direction
      }
    }
  }
`;

const DELETE_CONTACT_INTERACTION_MUTATION = gql`
  mutation DeleteContactInteraction($input: DeleteContactInteractionInput!) {
    deleteContactInteraction(input: $input) {
      success
    }
  }
`;

const createManualInteractionSchema = zod.object({
  type: zod.enum(MANUAL_INTERACTION_TYPES).nullable(),
  direction: zod.enum(['INBOUND', 'OUTBOUND']).nullable(),
  contactId: zod.number().nullable(),
  accountId: zod.number().nullable(),
  occurredAt: zod.string().min(1),
  description: zod.string().max(500).nullable(),
  departmentId: zod.number().nullable(),
});

type ManualInteractionFormValues = zod.infer<typeof createManualInteractionSchema>;

const InteractionTypeSelect = styled(InlineFormSelect)`
  .lgg-select__control {
    .lgg-select__single-value {
      font-family: ${({ theme }) => theme.font.regular};
      font-size: 18px;
      letter-spacing: -0.36px;
      line-height: 23px;
    }

    .lgg-select__placeholder {
      font-family: ${({ theme }) => theme.font.regular};
      font-size: 18px;
      letter-spacing: -0.36px;
      line-height: 23px;
    }
  }
`;

const InteractionTypeIcon = styled(Icon)`
  margin-right: 8px;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const DirectionOptionIcon = styled(Icon)`
  svg {
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.colors.smalt};
    }
  }
`;

const DirectionOptionLabel = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  letter-spacing: -0.12px;
  line-height: 15px;
`;

const DirectionValueLabel = styled(DirectionOptionLabel)`
  font-size: 13px;
`;

const DirectionOptionStrong = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: inherit;
  letter-spacing: -0.12px;
  line-height: 15px;
`;

const ExtraFieldOptionContainer = styled.div`
  margin-bottom: 20px;
`;

const getManualInteractionDefaultValues = (interaction: {
  occurredAt: string;
  description?: string | null;
  channel: { slug: string };
  department?: { id: number } | null;
  contact: { id: number };
  participatingEntity?: { id: string; type: string } | null;
  direction?: ContactInteractionDirection;
}): ManualInteractionFormValues => {
  const attendedByAccountId =
    interaction.participatingEntity?.type === 'AGENT'
      ? Number(interaction.participatingEntity.id)
      : null;

  return {
    type: interaction.channel.slug as ManualInteractionType,
    direction: interaction.direction ?? null,
    contactId: interaction.contact.id,
    accountId: attendedByAccountId,
    occurredAt: interaction.occurredAt,
    description: interaction.description ?? '',
    departmentId: interaction.department?.id ?? null,
  };
};

// Free-text inputs are saved when the user leaves them rather than while typing.
const AUTOSAVE_ON_BLUR_FIELDS: Path<ManualInteractionFormValues>[] = ['description'];

export const ManualInteractionInlineFormModal = memo(() => {
  const currentInstitution = useCurrentInstitution();
  const modalVisibilityHandler = useVisible(true);
  const { goBack } = useModalUrl({
    backgroundLocation: `/i/company/${currentInstitution.id}/activity`,
  });
  const { t } = useTranslation(['common', 'activity']);
  const currentUserId = useCurrentUserId();
  const handlecloseModal = () => {
    manualInteractionForm.reset();
    goBack();
    modalVisibilityHandler.close();
  };
  const { interactionId } = useParams<{
    interactionId?: string;
  }>();
  const isCreatingInteraction = !interactionId;
  const showNotification = useShowNotification();
  const { getEditContactInteractionUrl } = useUrls();
  const handleGraphQLError = useHandleGraphQLError();
  const showCloseConfirmationModal = useShowCloseConfirmationModal();
  const showConfirmationModal = useShowConfirmationModal();
  const { hasPermission } = useAuthorization();
  const hasContactInteractionManagePermission = useMemo(
    () => hasPermission('platform:company.communication_interaction:update:all'),
    [hasPermission],
  );
  const [isActivityOwner, setIsActivityOwner] = useState<boolean>(false);
  const { departmentOptions, loadingDepartmentOptions } =
    useActiveDepartmentSelectOptions();
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const targetContact = searchParams.get('contactId');
  const interactionChannelSlugRef = useRef<ManualInteractionType | null>(null);
  const interactionTypenameRef = useRef<string | undefined>(undefined);

  const handleGetInteractionError = useCallback(() => {
    modalVisibilityHandler.close();
    goBack();
    showNotification({
      title: t('activity:manualInteractionInlineForm.errors.notFound.title'),
      message: t('activity:manualInteractionInlineForm.errors.notFound.message'),
      type: 'error',
    });
  }, [modalVisibilityHandler, goBack, showNotification, t]);

  const [defaultValues, setDefaultValues] = useState<ManualInteractionFormValues>({
    type: null,
    direction: null,
    contactId: null,
    accountId: null,
    occurredAt: dayjs().toISOString(),
    description: '',
    departmentId: null,
  });

  const manualInteractionForm = useForm<ManualInteractionFormValues>({
    resolver: zodResolver(
      createManualInteractionSchema
        .extend({
          contactId: zod.number({
            message: t(
              'activity:manualInteractionInlineForm.fields.relatedContact.errors.required',
            ),
          }),
          accountId: zod.number({
            message: t(
              'activity:manualInteractionInlineForm.fields.attendedBy.errors.required',
            ),
          }),
        })
        .superRefine((values, ctx) => {
          if (values.type !== 'WALK_IN' && !values.direction) {
            ctx.addIssue({
              code: 'custom',
              path: ['direction'],
              message: t(
                'activity:manualInteractionInlineForm.fields.direction.errors.required',
              ),
            });
          }
        }),
    ),
    defaultValues,
  });

  useEffect(() => {
    if (!isCreatingInteraction) {
      return;
    }

    if (targetContact) {
      manualInteractionForm.setValue('contactId', Number(targetContact));
    }

    manualInteractionForm.setValue('accountId', currentUserId);
  }, [isCreatingInteraction, targetContact, currentUserId, manualInteractionForm]);

  const hasLoadedInteractionRef = useRef(false);

  const { loading: getInteractionLoading } = useQuery<
    Pick<Query, 'contactInteraction'>,
    QueryContactInteractionArgs
  >(GET_CONTACT_INTERACTION, {
    variables: { id: Number(interactionId) },
    skip: !interactionId,
    onCompleted: ({ contactInteraction }) => {
      if (!contactInteraction) {
        handleGetInteractionError();
        return;
      }

      if (hasLoadedInteractionRef.current) {
        return;
      }
      hasLoadedInteractionRef.current = true;

      const values = getManualInteractionDefaultValues(contactInteraction);
      setIsActivityOwner(contactInteraction.createdByUser?.id === currentUserId);

      interactionChannelSlugRef.current = values.type ?? null;
      interactionTypenameRef.current = contactInteraction.__typename;
      setDefaultValues(values);
      setShowDepartmentField(Boolean(values.departmentId));
      manualInteractionForm.reset(values);
    },
    onError: handleGetInteractionError,
  });

  const [createInteraction, { loading: createInteractionLoading }] = useMutation<
    Pick<Mutation, 'createManualContactInteraction'>,
    MutationCreateManualContactInteractionArgs
  >(CREATE_MANUAL_CONTACT_INTERACTION_MUTATION);
  const [updateInteraction] = useMutation<
    Pick<Mutation, 'updateContactInteraction'>,
    MutationUpdateContactInteractionArgs
  >(UPDATE_CONTACT_INTERACTION_MUTATION);
  const [deleteInteraction, { loading: deleteInteractionLoading }] = useMutation<
    Pick<Mutation, 'deleteContactInteraction'>,
    MutationDeleteContactInteractionArgs
  >(DELETE_CONTACT_INTERACTION_MUTATION);

  const {
    formState: { isDirty, dirtyFields },
  } = manualInteractionForm;

  const watchedType = manualInteractionForm.watch('type');

  const isFormDirty = isDirty && Object.keys(dirtyFields).length > 0;

  const typeOptions: SelectOption<ManualInteractionType>[] = [
    {
      label: t('activity:manualInteractionInlineForm.fields.type.options.call'),
      value: 'CALL',
      icon: 'call',
    },
    {
      label: t('activity:manualInteractionInlineForm.fields.type.options.email'),
      value: 'EMAIL',
      icon: 'email',
    },
    {
      label: t('activity:manualInteractionInlineForm.fields.type.options.sms'),
      value: 'SMS',
      icon: 'sms',
    },
    {
      label: t(
        'activity:manualInteractionInlineForm.fields.type.options.facebookMessenger',
      ),
      value: 'FACEBOOK_MESSENGER',
      icon: 'facebookMessenger',
    },
    {
      label: t('activity:manualInteractionInlineForm.fields.type.options.instagram'),
      value: 'INSTAGRAM',
      icon: 'instagram',
    },
    {
      label: t('activity:manualInteractionInlineForm.fields.type.options.whatsapp'),
      value: 'WHATSAPP',
      icon: 'whatsapp',
    },
    {
      label: t('activity:manualInteractionInlineForm.fields.type.options.inPerson'),
      value: 'WALK_IN',
      icon: 'inPerson',
    },
  ];

  const getDirectionTranslationKey = useCallback(
    (direction: ContactInteractionDirection) => {
      if (watchedType === 'CALL') {
        return direction === 'INBOUND'
          ? ('activity:manualInteractionInlineForm.fields.direction.options.incoming.call' as const)
          : ('activity:manualInteractionInlineForm.fields.direction.options.outgoing.call' as const);
      }

      return direction === 'INBOUND'
        ? ('activity:manualInteractionInlineForm.fields.direction.options.incoming.message' as const)
        : ('activity:manualInteractionInlineForm.fields.direction.options.outgoing.message' as const);
    },
    [watchedType],
  );

  const directionOptions = useMemo(() => {
    return [
      {
        value: 'INBOUND',
        icon: <DirectionOptionIcon type="incoming" />,
        label: 'INBOUND',
      },
      {
        value: 'OUTBOUND',
        icon: <DirectionOptionIcon type="outgoing" />,
        label: 'OUTBOUND',
      },
    ];
  }, []);

  const addFieldOptions = useMemo(() => {
    return showDepartmentField
      ? []
      : [
          {
            label: t('activity:manualInteractionInlineForm.fields.department.label'),
            'data-lgg-id': 'add-manual-interaction-extra-field-option-department',
            icon: <ExtraOptionIcon type="activityDepartment" />,
            onClick: () => {
              setShowDepartmentField(true);
            },
          },
        ];
  }, [showDepartmentField, t]);

  const updateInteractionRecord = useCallback(
    async (
      values: Partial<ManualInteractionFormValues>,
      { onCompleted }: { onCompleted: () => void },
    ) => {
      const isInPerson = interactionChannelSlugRef.current === 'WALK_IN';
      const { type: _type, contactId: _contactId, ...change } = values;

      await updateInteraction({
        variables: {
          id: Number(interactionId),
          input: isInPerson ? { inPerson: change } : { communication: change },
        },
        onCompleted,
        onError: handleGraphQLError,
      });
    },
    [updateInteraction, interactionId, handleGraphQLError],
  );

  const onAutosaveInteraction = useCallback(
    async (values: Partial<ManualInteractionFormValues>) => {
      await updateInteractionRecord(values, {
        onCompleted: () => {
          (Object.keys(values) as (keyof ManualInteractionFormValues)[]).forEach(
            (name) => {
              if (isEqual(manualInteractionForm.getValues(name), values[name])) {
                manualInteractionForm.resetField(name, { defaultValue: values[name] });
              }
            },
          );
        },
      });
    },
    [updateInteractionRecord, manualInteractionForm],
  );

  return (
    <InlineFormModal<ManualInteractionFormValues>
      title={
        isCreatingInteraction
          ? t('activity:manualInteractionInlineForm.title.create')
          : t('activity:manualInteractionInlineForm.title.update')
      }
      submitButtonText={t('activity:manualInteractionInlineForm.submitButtonText.create')}
      testId="upsert-manual-interaction-modal"
      isLoading={getInteractionLoading}
      isSubmitting={createInteractionLoading}
      visibilityHandler={modalVisibilityHandler}
      form={manualInteractionForm}
      onCancel={() => {
        if (!isFormDirty) {
          handlecloseModal();
          return;
        }

        showCloseConfirmationModal({
          onCancel: handlecloseModal,
        });
      }}
      onSubmit={async (values) => {
        await createInteraction({
          variables: {
            input: {
              institutionId: currentInstitution.id,
              contactId: values.contactId!,
              type: values.type!,
              accountId: values.accountId!,
              occurredAt: values.occurredAt,
              departmentId: values.departmentId ?? undefined,
              description: values.description,
              direction: values.type === 'WALK_IN' ? undefined : values.direction,
            },
          },
          onCompleted: () => {
            showNotification({
              title: t('activity:manualInteractionInlineForm.notifications.create'),
              type: 'success',
            });
            handlecloseModal();
          },
          onError: handleGraphQLError,
        });
      }}
      autoSaveEnabled={!isCreatingInteraction}
      onAutosave={onAutosaveInteraction}
      autosaveOnBlurFields={AUTOSAVE_ON_BLUR_FIELDS}
      moreOptions={
        isCreatingInteraction
          ? []
          : [
              {
                icon: 'link',
                label: t(
                  'activity:manualInteractionInlineForm.moreOptions.copyLink.label',
                ),
                'data-lgg-id': 'manual-interaction-more-options-copy-link',
                onClick: () => {
                  copyUrlToClipboard(getEditContactInteractionUrl(Number(interactionId)));
                  showNotification({
                    title: t(
                      'activity:manualInteractionInlineForm.moreOptions.copyLink.successMessage',
                    ),
                    type: 'success',
                  });
                },
              },
              ...(hasContactInteractionManagePermission || isActivityOwner
                ? [
                    {
                      icon: 'deleteBin',
                      label: t(
                        'activity:manualInteractionInlineForm.moreOptions.deleteInteraction.label',
                      ),
                      'data-lgg-id': 'manual-interaction-more-options-delete',
                      disabled: deleteInteractionLoading,
                      onClick: () => {
                        showConfirmationModal({
                          title: t(
                            'activity:manualInteractionInlineForm.moreOptions.deleteInteraction.confirmationTitle',
                          ),
                          message: t(
                            'activity:manualInteractionInlineForm.moreOptions.deleteInteraction.confirmationMessage',
                          ),
                          confirmButtonText: t(
                            'activity:manualInteractionInlineForm.moreOptions.deleteInteraction.label',
                          ),
                          confirmButtonType: 'delete',
                          testId: 'delete-manual-interaction-confirmation-modal',
                          onConfirm: async () => {
                            await deleteInteraction({
                              variables: {
                                input: {
                                  contactInteractionId: Number(interactionId),
                                },
                              },
                              onCompleted: () => {
                                showNotification({
                                  title: t(
                                    'activity:manualInteractionInlineForm.moreOptions.deleteInteraction.successMessage',
                                  ),
                                  type: 'success',
                                });
                                handlecloseModal();
                              },
                              onError: handleGraphQLError,
                              update: (cache) => {
                                const normalizedId = cache.identify({
                                  id: Number(interactionId),
                                  __typename: interactionTypenameRef.current!,
                                });
                                cache.evict({ id: normalizedId });
                                cache.gc();
                              },
                            });
                          },
                        });
                      },
                    },
                  ]
                : []),
            ]
      }
      topContent={
        <FlexRow style={{ padding: '20px', alignItems: 'center' }}>
          <InteractionTypeIcon type="interaction" />
          <Controller
            control={manualInteractionForm.control}
            name="type"
            render={({ field }) => {
              const error = manualInteractionForm.formState.errors.type?.message;

              return (
                <InteractionTypeSelect
                  name="upsert-manual-interaction-field-type"
                  mobileLabel={t(
                    'activity:manualInteractionInlineForm.fields.type.label',
                  )}
                  value={typeOptions.find((option) => option.value === field.value)}
                  hasError={Boolean(error)}
                  isClearable={false}
                  isSearchable={false}
                  options={typeOptions}
                  placeholder={t(
                    'activity:manualInteractionInlineForm.fields.type.placeholder',
                  )}
                  hideIndicators
                  onChange={(value) => {
                    field.onChange((value as SelectOption<ManualInteractionType>)?.value);
                  }}
                  renderValue={(value) => {
                    return value?.label;
                  }}
                />
              );
            }}
          />
        </FlexRow>
      }
    >
      <FlexColumn>
        {manualInteractionForm.watch('type') !== 'WALK_IN' && (
          <Controller
            control={manualInteractionForm.control}
            name="direction"
            render={({ field }) => {
              const error = manualInteractionForm.formState.errors.direction?.message;

              return (
                <InlineInputWrapper
                  label={t('activity:manualInteractionInlineForm.fields.direction.label')}
                  error={error}
                  icon="interactionDirectionAlt"
                  showErrorMessage={false}
                >
                  <InlineFormSelect
                    name="upsert-manual-interaction-field-direction"
                    mobileLabel={t(
                      'activity:manualInteractionInlineForm.fields.direction.label',
                    )}
                    value={directionOptions.find(
                      (option) => option.value === field.value,
                    )}
                    hasError={Boolean(error)}
                    isClearable={false}
                    isSearchable={false}
                    options={directionOptions}
                    isDisabled={!watchedType}
                    renderOption={(option) => {
                      return (
                        <FlexRow style={{ gap: '10px', alignItems: 'center' }}>
                          {option.icon}
                          <DirectionOptionLabel>
                            <Trans
                              i18nKey={getDirectionTranslationKey(
                                option.value as ContactInteractionDirection,
                              )}
                              components={{ strong: <DirectionOptionStrong /> }}
                            />
                          </DirectionOptionLabel>
                        </FlexRow>
                      );
                    }}
                    renderValue={(value) => {
                      return (
                        <DirectionValueLabel>
                          <Trans
                            i18nKey={getDirectionTranslationKey(
                              value?.value as ContactInteractionDirection,
                            )}
                            components={{ strong: <DirectionOptionStrong /> }}
                          />
                        </DirectionValueLabel>
                      );
                    }}
                    hideIndicators
                    onChange={(value) => {
                      field.onChange(
                        (value as SelectOption<ContactInteractionDirection>)?.value,
                      );
                    }}
                  />
                </InlineInputWrapper>
              );
            }}
          />
        )}
        <Controller
          control={manualInteractionForm.control}
          name="contactId"
          render={({ field }) => {
            const error = manualInteractionForm.formState.errors.contactId?.message;

            return (
              <InlineInputWrapper
                label={t(
                  'activity:manualInteractionInlineForm.fields.relatedContact.label',
                )}
                error={error}
                icon="contact"
                showErrorMessage={false}
              >
                <InlineFormContactSelect
                  selectedContactIds={field.value ? [field.value] : []}
                  isMulti={false}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value.value === field.value ? null : value.value);
                    }
                  }}
                  isDisabled={!isCreatingInteraction}
                  hasError={Boolean(error)}
                  isClearable={false}
                  name="upsert-manual-interaction-field-related-contact"
                  mobileLabel={t(
                    'activity:manualInteractionInlineForm.fields.relatedContact.label',
                  )}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={manualInteractionForm.control}
          name="accountId"
          render={({ field }) => {
            const error = manualInteractionForm.formState.errors.accountId?.message;

            return (
              <InlineInputWrapper
                label={t('activity:manualInteractionInlineForm.fields.attendedBy.label')}
                error={error}
                icon="assignedto"
                showErrorMessage={false}
              >
                <InlineFormUserSelect
                  mobileLabel={t(
                    'activity:manualInteractionInlineForm.fields.attendedBy.label',
                  )}
                  selectedUsersIds={field.value ? [field.value] : []}
                  hasError={Boolean(error)}
                  isMulti={false}
                  isClearable={false}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value.value === field.value ? null : value.value);
                    }
                  }}
                  name="upsert-manual-interaction-field-attended-by"
                  hideIndicators
                  renderValue={(value) => <UserSelectValue value={value} />}
                  renderPlaceholder={() => (
                    <UserSelectPlaceholder
                      placeholder={t(
                        'activity:manualInteractionInlineForm.fields.attendedBy.placeholder',
                      )}
                    />
                  )}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={manualInteractionForm.control}
          name="occurredAt"
          render={({ field }) => {
            const error = manualInteractionForm.formState.errors.occurredAt?.message;

            return (
              <InlineInputWrapper
                label={t(
                  'activity:manualInteractionInlineForm.fields.occurredTime.label',
                )}
                error={error}
                showErrorMessage={false}
                icon="calendarNoPadding"
              >
                <LggDateTimePicker
                  showPresetOptions
                  allowTimeSelection
                  inputValue={field.value ? dayjs(field.value).toDate() : undefined}
                  inputDateFormat="MMM D, YYYY. hh:mm a"
                  onApply={(date) => {
                    field.onChange(date ? date.toISOString() : undefined);
                  }}
                  placeholder={t(
                    'activity:manualInteractionInlineForm.fields.occurredTime.placeholder',
                  )}
                  testId="upsert-manual-interaction-field-occurred-time"
                  hasError={Boolean(error)}
                  disabledDate={(date) => date.isAfter(dayjs())}
                  allowClear={false}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={manualInteractionForm.control}
          name="description"
          render={({ field }) => {
            const error = manualInteractionForm.formState.errors.description?.message;

            return (
              <InlineTextArea
                {...field}
                label={t('activity:manualInteractionInlineForm.fields.description.label')}
                icon="draft"
                error={error}
                placeholder={t(
                  'activity:manualInteractionInlineForm.fields.description.placeholder',
                )}
                data-lgg-id="upsert-manual-interaction-field-description"
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                onBlur={(e) => {
                  e.currentTarget.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            );
          }}
        />
        {showDepartmentField && (
          <Controller
            control={manualInteractionForm.control}
            name="departmentId"
            render={({ field }) => {
              const error = manualInteractionForm.formState.errors.departmentId?.message;

              return (
                <InlineInputWrapper
                  label={t(
                    'activity:manualInteractionInlineForm.fields.department.label',
                  )}
                  error={error}
                  icon="activityDepartment"
                  showErrorMessage={false}
                >
                  <InlineFormSelect
                    key={field.value ? 'has-value' : 'no-value'}
                    name="upsert-manual-interaction-field-department"
                    mobileLabel={t(
                      'activity:manualInteractionInlineForm.fields.department.label',
                    )}
                    value={departmentOptions.find(
                      (option) => option.value === field.value,
                    )}
                    options={departmentOptions}
                    isLoading={loadingDepartmentOptions}
                    placeholder={t(
                      'activity:manualInteractionInlineForm.fields.department.placeholder',
                    )}
                    isSearchable
                    isClearable={false}
                    hideIndicators
                    onChange={(value) => {
                      field.onChange(value?.value === field.value ? null : value?.value);
                    }}
                  />
                </InlineInputWrapper>
              );
            }}
          />
        )}
      </FlexColumn>
      {addFieldOptions.length > 0 && (
        <ExtraFieldOptionContainer>
          <AddExtraFieldButton options={addFieldOptions} />
        </ExtraFieldOptionContainer>
      )}
    </InlineFormModal>
  );
});
